import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown } from "lucide-react";

interface Game {
  buyin_amount: number;
  chips_per_buyin: number;
  status: string;
}

interface Player {
  id: string;
  name: string;
}

interface Buyin {
  player_id: string;
  amount: number;
  chips: number;
}

interface ChipEntry {
  player_id: string;
  final_chips: number;
}

interface GameSummaryProps {
  game: Game;
  players: Player[];
  buyins: Buyin[];
  chipEntries: ChipEntry[];
  pointsByPlayer?: Record<string, number>;
}

export const GameSummary = ({ game, players, buyins, chipEntries, pointsByPlayer }: GameSummaryProps) => {
  const chipRatio = game.buyin_amount / game.chips_per_buyin;

  const getPlayerStats = (player: Player) => {
    const playerBuyins = buyins.filter(b => b.player_id === player.id);
    const totalSpent = playerBuyins.reduce((sum, buyin) => sum + buyin.amount, 0);
    const totalChipsBought = playerBuyins.reduce((sum, buyin) => sum + buyin.chips, 0);
    
    const chipEntry = chipEntries.find(ce => ce.player_id === player.id);
    const finalChips = chipEntry?.final_chips || 0;
    
    const netChips = finalChips - totalChipsBought;
    const netAmount = netChips * chipRatio;
    
    return {
      totalSpent,
      totalChipsBought,
      finalChips,
      netChips,
      netAmount,
      hasEnteredChips: !!chipEntry
    };
  };

  const playersWithStats = players.map(player => ({
    ...player,
    stats: getPlayerStats(player)
  })).sort((a, b) => b.stats.netAmount - a.stats.netAmount);

  const totalPot = buyins.reduce((sum, buyin) => sum + buyin.amount, 0);
  const totalChipsInPlay = buyins.reduce((sum, buyin) => sum + buyin.chips, 0);

  return (
    <div className="space-y-6">
      {/* Game Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Game Overview</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Total Pot</p>
            <p className="text-lg font-semibold">₹{totalPot.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Chips</p>
            <p className="text-lg font-semibold">{totalChipsInPlay.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Chip Ratio</p>
            <p className="text-lg font-semibold">{chipRatio.toFixed(4)}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Status</p>
            <Badge variant={game.status === 'in_progress' ? 'default' : 'secondary'}>
              {game.status === 'in_progress' ? 'Active' : 'Settled'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Player Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Player Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {playersWithStats.map((player) => (
              <div key={player.id} className="flex flex-col md:flex-row md:items-center md:justify-between p-4 bg-muted/50 rounded scale-90 md:scale-100">
                <div className="flex items-center space-x-3 mb-2 md:mb-0">
                  <span className="font-medium">{player.name}</span>
                  {!player.stats.hasEnteredChips && game.status === 'in_progress' && (
                    <Badge variant="outline" className="text-xs">
                      Chips not entered
                    </Badge>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                  <div className="text-center">
                    <p className="text-muted-foreground text-xs">Spent</p>
                    <p className="font-medium">₹{player.stats.totalSpent}</p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-muted-foreground text-xs">Total Chips Buy-in</p>
                    <p className="font-medium">{player.stats.totalChipsBought.toLocaleString()}</p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-muted-foreground text-xs">Final Chips</p>
                    <p className="font-medium">
                      {player.stats.hasEnteredChips 
                        ? player.stats.finalChips.toLocaleString() 
                        : 'Not entered'
                      }
                    </p>
                  </div>
                  
                  {player.stats.hasEnteredChips && (
                    <div className="text-center">
                      <p className="text-muted-foreground text-xs">Net P&L</p>
                      <div className="flex items-center justify-center space-x-1">
                        {player.stats.netAmount > 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-500" />
                        ) : player.stats.netAmount < 0 ? (
                          <TrendingDown className="h-4 w-4 text-red-500" />
                        ) : null}
                        <p className={`font-medium ${
                          player.stats.netAmount > 0 
                            ? 'text-green-600' 
                            : player.stats.netAmount < 0 
                            ? 'text-red-600' 
                            : 'text-muted-foreground'
                        }`}>
                          {player.stats.netAmount > 0 ? '+' : ''}₹{player.stats.netAmount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}

                  {(() => {
                    const pts = game.status === 'settled' ? pointsByPlayer?.[player.id] : undefined;
                    return (
                      <div className="text-center">
                        <p className="text-muted-foreground text-xs">Points</p>
                        <p className={`font-medium ${
                          pts === undefined || pts === 0
                            ? 'text-muted-foreground'
                            : pts > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {pts === undefined ? '-' : `${pts > 0 ? '+' : ''}${pts}`}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};